import { useAtomValue } from 'jotai';
import React from 'react';
import { Tabs, TabHeader, TabPanel } from '@wk/components-react16';
import { ButtonField, Icon, Tooltip } from '@wk/components-v3-react16';
import { workflowAtom } from '../atoms';
import { useSidePanel } from '../SidePanel/useSidePanel';
import JsonViewTab from './JsonViewTab/JsonViewTab';
import { useSettingsCanvas } from './useSettingsCanvas';
import { useWorkflowTabs } from './useWorkflowTabs';
import styles from './WorkflowTabs.module.scss';
import WorkflowCanvasWithLayout from '../DroppableCanvas/WorkflowCanvasWithLayout/WorkflowCanvasWithLayout';
import LegendTab from './LegendTab/LegendTab';
import XMLCodeTab from './XMLCodeTab/XMLCodeTab';
import { useSaveWorkflow } from './useSaveWorkflow';

const WorkflowTabs: React.FC = () => {
  const { selectedTab, handleTabChange } = useWorkflowTabs();
  const { isMousePressed, handleMouseUp, handleMouseDown } =
    useSettingsCanvas();
  const workflow = useAtomValue(workflowAtom);
  const {
    hideDropZoneIcons,
    hideDropZoneText,
    hideStep,
    disableBackgroundColor,
    isCompact,
    toggleBackgroundColor,
    toggleCompact,
    toggleDropZoneIcons,
    toggleDropZoneText,
    toggleHideStep,
  } = useSettingsCanvas();
  const { isLoading, saveWorkflow } = useSaveWorkflow();
  const { toggleSidePanel, isSidePanelOpen } = useSidePanel();
  const name = workflow.name;
  const nestedGroup = workflow.nextStepGroup;
  const saveDisabled = !name || !nestedGroup || isLoading;
  const TOOLTIP_MESSAGES = {
    DISABLED:
      "To save your Orchestration, enter an 'Orchestration Name', drag-and-drop at least one Module onto the design canvas, and populate all mandatory property fields",
    ENABLED:
      'To save your Orchestration during the design phase, complete all mandatory property fields for the Orchestration and Modules.',
  };

  const renderSaveButton = () => (
    <>
      <ButtonField type={isSidePanelOpen ? 'primary' : 'secondary'}>
        <button onClick={toggleSidePanel}>
          Properties <Icon name="wk-icon-settings" />
        </button>
      </ButtonField>
      <ButtonField type={isCompact ? 'secondary' : 'primary'}>
        <button onClick={toggleCompact}>
          Layers <Icon name="wk-icon-layer" />
        </button>
      </ButtonField>
      <ButtonField type={disableBackgroundColor ? 'secondary' : 'primary'}>
        <button onClick={toggleBackgroundColor}>
          BackGround <Icon name="wk-icon-stop" />
        </button>
      </ButtonField>
      <ButtonField type={hideDropZoneIcons ? 'secondary' : 'primary'}>
        <button onClick={toggleDropZoneIcons}>
          Tags <Icon name="wk-icon-tag" />
        </button>
      </ButtonField>
      <ButtonField type={hideDropZoneText ? 'secondary' : 'primary'}>
        <button onClick={toggleDropZoneText}>
          Text <Icon name="wk-icon-document-signed" />
        </button>
      </ButtonField>
      <ButtonField type={hideStep ? 'secondary' : 'primary'}>
        <button onClick={toggleHideStep}>
          Info <Icon name="wk-icon-articles" />
        </button>
      </ButtonField>
      <ButtonField type="primary">
        <button
          onClick={() => saveWorkflow()}
          disabled={saveDisabled}
          id="saveButton"
        >
          {isLoading ? (
            <>
              Saving...
              <Icon name="wk-icon-sync" isSpin />
            </>
          ) : (
            <div className={styles.textIcon}>
              <span>Save</span>
              <Icon name="wk-icon-save" />
            </div>
          )}
        </button>
      </ButtonField>
      <Tooltip
        targetSelector="#saveButton"
        elementId="tooltip"
        mode="description"
        position="top"
      >
        <span className={styles.tooltipText}>
          {saveDisabled ? TOOLTIP_MESSAGES.DISABLED : TOOLTIP_MESSAGES.ENABLED}
        </span>
      </Tooltip>
    </>
  );
  return (
    <>
      <div className={styles.container}>
        <Tabs
          mode="classic"
          selectedTab={selectedTab}
          onUserRequest={(event: number) => handleTabChange(event)}
          controlMode="controlled"
        >
          <TabHeader slot="tabHeader">
            <div>Pipeline Sequence</div>
          </TabHeader>
          <TabHeader slot="tabHeader">
            <div>Pipeline Global Failback Steps</div>
          </TabHeader>
          <TabHeader slot="tabHeader">
            <div>XML Code</div>
          </TabHeader>
          <TabHeader slot="tabHeader">
            <div>JSON</div>
          </TabHeader>
          <TabHeader slot="tabHeader">
            <div>Legend</div>
          </TabHeader>

          <TabPanel slot="tabPanel">
            <div className={styles.sectionTitle}>
              Pipeline Sequence
              {renderSaveButton()}
            </div>
          </TabPanel>
          <TabPanel slot="tabPanel">
            <div className={styles.sectionTitle}>
              Pipeline Global Failback Steps
              {renderSaveButton()}
            </div>
          </TabPanel>
          <TabPanel slot="tabPanel">
            <XMLCodeTab />
          </TabPanel>
          <TabPanel slot="tabPanel">
            <JsonViewTab />
          </TabPanel>
          <TabPanel slot="tabPanel">
            <LegendTab />
          </TabPanel>
        </Tabs>
        <span
          onMouseUp={handleMouseUp}
          onMouseDown={handleMouseDown}
          className={styles.underContainer}
          style={{
            cursor: isMousePressed ? 'grabbing' : 'grab',
          }}
        >
          <WorkflowCanvasWithLayout />
        </span>
      </div>
    </>
  );
};

export default WorkflowTabs;
